import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type {
	Media,
	OriginalStimulusTypes,
	Professor,
	SkillTypes,
	Slide,
} from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import ImagesCarousel from "@memorang/ui/src/components/ImagesCarousel";
import InfoAlert from "components/InfoAlert";
import { ScrollView } from "react-native";
import { Text } from "react-native-paper";
import AudioPlayerContainer from "../containers/AudioPlayerContainer";
import DiscussionView from "./DiscussionView";
import ItemStem from "./ItemStem";
import { PassageStem } from "./PassageStem";
import SlideShowRenderer from "./SlideShowRenderer";
import StimulusInfo from "./StimulusInfo";
import StimulusTabContent from "./StimulusTabContent";

type Props = {
	stem: string;
	audio?: string;
	toggleShowReadMore?: () => void;
	highlightedHtml?: string;
	id?: number;
	handleStoreHtml?: (html: string) => void;
	isSummativeTest?: boolean;
	index?: number;
	handleClickInsertableText: (choiceId: string) => void;
	numPassageItems?: number;
	parentTitle?: string;
	parentInstruction?: string;
	parentType: OriginalStimulusTypes;
	skill: SkillTypes;
	disableInsertableText?: boolean;
	slideshowData?: { image: string; audio: string }[];
	professor?: Professor;
	slides?: Slide[];
	isSlidesHasPassage?: boolean;
	parentImages?: Media[];
};
const PassageStemContainer = ({
	stem,
	audio,
	toggleShowReadMore,
	highlightedHtml,
	id,
	index,
	isSummativeTest,
	handleClickInsertableText,
	numPassageItems,
	parentTitle,
	parentInstruction,
	parentType,
	slideshowData,
	professor,
	slides,
	isSlidesHasPassage,
	skill,
	disableInsertableText,
	parentImages,
}: Props) => {
	const { isMobile, isMediumWindowSize } = useDevice();
	const theme = useAppTheme();

	if (isMobile || isMediumWindowSize) {
		return (
			<PassageStem
				parentStem={stem}
				index={index}
				numPassageItems={numPassageItems}
				parentTitle={parentTitle}
				toggleShowReadMore={toggleShowReadMore}
				parentType={parentType}
				itemId={id!}
			/>
		);
	}

	return (
		<ScrollView showsVerticalScrollIndicator={false}>
			<Box height={"100%"} gap={16} style={{ overflowY: "auto" }}>
				<Box
					gap={16}
					paddingBottom={100}
					height={"100%"}
					paddingTop={isSummativeTest ? 16 : 0}
				>
					{isSlidesHasPassage && slides && slides.length > 0 ? (
						<StimulusTabContent
							padding={0}
							parentTitle={parentTitle!}
							slideshowData={slideshowData!}
							parentIndex={index!}
							slides={slides!}
							parentInstruction={parentInstruction!}
							itemId={id}
						/>
					) : (
						<>
							<StimulusInfo skill={skill} />
							{parentInstruction && index === 1 && (
								<InfoAlert
									text={parentInstruction}
									backgroundColor={theme.colors.primaryContainer}
								/>
							)}
							<Text variant="titleLarge">{parentTitle}</Text>
							{professor && <DiscussionView professor={professor} />}

							{slideshowData && slideshowData.length > 0 && (
								<SlideShowRenderer slideshowData={slideshowData} itemId={id} />
							)}
							{stem && (
								<ItemStem
									text={stem}
									highlightedHtml={highlightedHtml}
									key={id}
									handleClickInsertableText={handleClickInsertableText}
									isSummativeTest={isSummativeTest}
									disableInsertableText={disableInsertableText}
								/>
							)}

							{audio && <AudioPlayerContainer uri={audio} itemId={id} />}
							{parentImages && (
								<ImagesCarousel
									images={parentImages}
									contentFit="contain"
									customImageHeight={parentImages.length > 1 ? 190 : 420}
									customImageWidth={parentImages.length > 1 ? 190 : 600}
								/>
							)}
						</>
					)}
				</Box>
			</Box>
		</ScrollView>
	);
};

export default PassageStemContainer;
